import { motion } from "framer-motion"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { Table } from "../../components/Table"
import { SidePane } from "../../components/SidePane"
import { Tabs } from "../../components/Tabs"
import { useContactLogs, useInvoices, useIssueBalanceNow, useResendPayLink } from "../../hooks/useApi"
import type { ContactLogs, Invoice, InvoiceStatus } from "../../types"
import { useControlCenter } from "../../AppShell"
import { rememberLastContact } from "../../constants/storage"
import { useThemeSurfaces, type ButtonTone } from "../../theme/useThemeSurfaces"

const amountFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
})

const tabs: Array<{ id: InvoiceStatus | ""; label: string }> = [
  { id: "", label: "All" },
  { id: "sent", label: "Sent" },
  { id: "paid", label: "Paid" },
  { id: "overdue", label: "Overdue" },
]

export default function SessionsPage() {
  const [status, setStatus] = useState<InvoiceStatus | "">("")
  const [query, setQuery] = useState("")
  const [searchFocused, setSearchFocused] = useState(false)
  const [selected, setSelected] = useState<Invoice | null>(null)
  const navigate = useNavigate()

  const invoicesQuery = useInvoices({ status, query })
  const invoices = useMemo(
    () => invoicesQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [invoicesQuery.data],
  )
  const resend = useResendPayLink()
  const issue = useIssueBalanceNow()
  const { tenant } = useControlCenter()
  const logs = useContactLogs(selected?.contact_id)
  const surfaces = useThemeSurfaces()
  const inputTokens = surfaces.input
  const outlineButton = surfaces.button.outline
  const mutedColor = surfaces.panel.primary.muted

  const handleMessageContact = (contactId: string) => {
    if (!contactId) return
    rememberLastContact(contactId)
    navigate(`/app/customers?contactId=${encodeURIComponent(contactId)}`)
  }

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "/" && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        const input = document.getElementById("sessions-search") as HTMLInputElement | null
        input?.focus()
        setSearchFocused(true)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  useEffect(() => {
    if (!searchFocused) return
    const input = document.getElementById("sessions-search") as HTMLInputElement | null
    input?.focus()
  }, [searchFocused])

  const columns = [
    {
      key: "status",
      title: "Status",
      render: (row: Invoice) => <StatusChip status={row.status} />,
      mobileRole: "meta" as const,
    },
    {
      key: "amount_cents",
      title: "Amount",
      render: (row: Invoice) => amountFormatter.format(row.amount_cents / 100),
      mobileRole: "value" as const,
    },
    {
      key: "customer",
      title: "Customer",
      mobileRole: "title" as const,
    },
    {
      key: "date_iso",
      title: "Date",
      render: (row: Invoice) => formatDate(row.date_iso),
      mobileRole: "meta" as const,
    },
    {
      key: "provider_id",
      title: "Provider ID",
      mobileRole: "meta" as const,
    },
  ]

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(16px, 3vw, 24px)" }}>
      {!tenant.payments_ready ? (
        <section
          style={{
            background: "rgba(255,43,79,0.95)",
            border: "2px solid #0a0a0b",
            padding: "clamp(20px, 4vw, 28px)",
            display: "grid",
            gap: "12px",
            boxShadow: "0 0 32px rgba(255,43,79,0.35)",
          }}
        >
          <header className="mono" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "#0a0a0b" }}>
            Connect Stripe
          </header>
          <p className="small" style={{ textTransform: "none", color: "#0a0a0b", lineHeight: 1.5 }}>
            Connect Stripe to enable invoice features. Visit the Dashboard to complete setup.
          </p>
          <button
            type="button"
            onClick={() => navigate("/app/dashboard")}
            className="mono"
            style={{
              border: "2px solid #0a0a0b",
              padding: "12px 16px",
              background: "#0a0a0b",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
              color: "#fff",
              minHeight: "44px",
              justifySelf: "flex-start",
            }}
          >
            Go to Dashboard
          </button>
        </section>
      ) : null}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Tabs tabs={tabs} activeId={status} onChange={(id) => setStatus(id as "" | InvoiceStatus)} />
        <div style={{ display: "flex", gap: "12px" }}>
          <ExportButton label="Export CSV" outlineButton={outlineButton} />
          <ExportButton label="Export JSON" outlineButton={outlineButton} />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <input
          id="sessions-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="/ search invoices"
          className="mono"
          style={{
            padding: "12px 18px",
            border: inputTokens.border,
            background: inputTokens.background,
            color: inputTokens.color,
            letterSpacing: "0.12em",
            width: "min(100%, 520px)",
          }}
        />
        <Table
          data={invoices}
          columns={columns}
          getRowId={(row) => row.id}
          activeRowId={selected?.id ?? null}
          onRowClick={(row) => setSelected(row)}
          enableKeyboardNav
          ariaLabel="Invoice sessions"
        />
        {invoicesQuery.isFetching ? <div className="small">Loading invoices…</div> : null}
        {invoicesQuery.hasNextPage ? (
          <motion.button
            type="button"
            whileTap={{ scale: 0.95 }}
            onClick={() => invoicesQuery.fetchNextPage()}
            disabled={invoicesQuery.isFetchingNextPage}
            className="mono"
            style={{
              alignSelf: "flex-start",
              padding: "10px 16px",
              border: outlineButton.border,
              borderRadius: "12px",
              background: outlineButton.background,
              color: outlineButton.color,
              letterSpacing: "0.12em",
              textTransform: "uppercase" as const,
              cursor: invoicesQuery.isFetchingNextPage ? "progress" : "pointer",
              minHeight: "44px",
              boxShadow: outlineButton.shadow,
            }}
          >
            {invoicesQuery.isFetchingNextPage ? "Loading…" : "Load more"}
          </motion.button>
        ) : null}
      </div>

      <SidePane
        paneKey={selected ? `invoice-${selected.id}` : undefined}
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Invoice overview"
        actions={
          selected ? (
            <div className="detail-actions">
              {selected.contact_id ? (
                <ActionButton
                  label="Message contact"
                  loading={false}
                  onClick={() => handleMessageContact(selected.contact_id!)}
                  outlineButton={outlineButton}
                />
              ) : null}
              <ActionButton
                label="Resend pay link"
                loading={resend.isPending}
                disabled={!selected.contact_id}
                onClick={() =>
                  selected &&
                  resend.mutate({
                    invoiceId: selected.id,
                    contactId: selected.contact_id,
                    tenantId: tenant.id ?? 1,
                  })
                }
                outlineButton={outlineButton}
              />
              <ActionButton
                label="Send balance invoice"
                loading={issue.isPending}
                disabled={!selected.contact_id}
                onClick={() =>
                  selected &&
                  selected.contact_id &&
                  issue.mutate({
                    invoiceId: selected.id,
                    contactId: selected.contact_id,
                    tenantId: tenant.id ?? 1,
                  })
                }
                outlineButton={outlineButton}
              />
            </div>
          ) : null
        }
      >
        {selected ? (
          <div className="detail-pane" key={selected.id}>
            <section className="detail-pane__hero">
              <div className="detail-pane__heading">
                <span className="detail-pane__eyebrow">Invoice</span>
                <h2 className="detail-pane__title">{selected.customer}</h2>
                <div className="detail-pane__meta">
                  <span>Issued {formatDate(selected.date_iso)}</span>
                  <span>#{selected.provider_id}</span>
                  {selected.contact_id ? <span>Contact {selected.contact_id}</span> : null}
                </div>
              </div>
              <div className="detail-pane__badge">
                <span className={`detail-pane__status detail-pane__status--${selected.status}`}>
                  {selected.status.toUpperCase()}
                </span>
                <span className="detail-pane__amount">{amountFormatter.format(selected.amount_cents / 100)}</span>
              </div>
            </section>

            <div className="detail-pane__grid">
              <InfoRow label="Customer" value={selected.customer} />
              {selected.hosted_invoice_url ? (
                <InfoRow
                  label="Pay Link"
                  value={
                    <a
                      href={selected.hosted_invoice_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "var(--accent-2-text)", textDecoration: "underline" }}
                    >
                      Open invoice
                    </a>
                  }
                />
              ) : null}
              <InfoRow label="Status" value={selected.status.toUpperCase()} />
              <InfoRow label="Issued" value={formatDate(selected.date_iso)} />
            </div>

            <div className="detail-pane__divider" />

            <div style={{ display: "grid", gap: "12px" }}>
              <span className="small" style={{ color: mutedColor, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Ops timeline
              </span>
              <OpsTimeline logs={logs.data} loading={logs.isLoading} />
            </div>

            <div className="small" style={{ color: mutedColor }}>
              Web send toggle is off — use SMS to deliver invoices.
            </div>
          </div>
        ) : (
          <div className="small">Select an invoice to view details.</div>
        )}
      </SidePane>
    </div>
  )
}

function ExportButton({ label, outlineButton }: { label: string; outlineButton: ButtonTone }) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      className="mono"
      style={{
        border: outlineButton.border,
        padding: "10px 16px",
        background: outlineButton.background,
        color: outlineButton.color,
        letterSpacing: "0.12em",
        textTransform: "uppercase" as const,
        cursor: "pointer",
        minHeight: "44px",
        boxShadow: outlineButton.shadow,
      }}
    >
      {label}
    </motion.button>
  )
}

function StatusChip({ status }: { status: Invoice["status"] }) {
  const colors: Record<Invoice["status"], string> = {
    sent: "var(--rail)",
    paid: "#31d0aa",
    overdue: "#ff7a1a",
  }
  return (
    <span className="small" style={{ color: colors[status] }}>
      {status.toUpperCase()}
    </span>
  )
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  const mutedColor = useThemeSurfaces().panel.primary.muted

  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
      <span className="small" style={{ color: mutedColor, textTransform: "uppercase" as const }}>
        {label}
      </span>
      <span className="mono" style={{ textAlign: "right" }}>
        {value}
      </span>
    </div>
  )
}

function ActionButton({
  label,
  loading,
  disabled,
  onClick,
  outlineButton,
}: {
  label: string
  loading: boolean
  disabled?: boolean
  onClick: () => void
  outlineButton: ButtonTone
}) {
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      disabled={loading || disabled}
      className="mono"
      style={{
        border: outlineButton.border,
        padding: "8px 12px",
        background: outlineButton.background,
        color: outlineButton.color,
        letterSpacing: "0.12em",
        textTransform: "uppercase" as const,
        cursor: loading || disabled ? "not-allowed" : "pointer",
        opacity: loading || disabled ? 0.6 : 1,
        minHeight: "44px",
        width: "100%",
        boxShadow: outlineButton.shadow,
      }}
    >
      {loading ? "Working…" : label}
    </motion.button>
  )
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleDateString()
  } catch {
    return value
  }
}

function OpsTimeline({ logs, loading }: { logs?: ContactLogs; loading: boolean }) {
  if (loading) {
    return <div className="small">Loading ops log…</div>
  }
  if (!logs || logs.ops.length === 0) {
    return <div className="small">Ops history will appear once actions run.</div>
  }
  return (
    <div style={{ display: "grid", gap: "8px", maxHeight: "200px", overflowY: "auto", paddingRight: "8px" }}>
      {logs.ops.map((entry) => (
        <div key={entry.at + entry.detail} style={{ display: "grid", gap: "4px" }}>
          <span className="small" style={{ color: "var(--muted)" }}>{new Date(entry.at).toLocaleString()}</span>
          <span className="mono" style={{ whiteSpace: "pre-wrap" }}>{entry.detail}</span>
        </div>
      ))}
    </div>
  )
}
